import type { IModuleService, ResolvedView } from '@hibro/shared';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { useTranslator } from '../../i18n/i18n';
import type { UiRegistry } from '../../runtime/ui-registry';
import { DetachBridge } from '../../surfaces/detach';
import { isDetachedInstance } from '../../surfaces/detached-views';
import type { ExternalBridge } from '../../surfaces/external-surface';
import type { IframeBridge } from '../../surfaces/iframe-surface';
import { SurfaceView } from '../../surfaces/surface-host';
import { DetachedPlaceholder } from '../detached-placeholder';
import {
  planMountedInstances,
  removeInstances,
  renderOrder,
  resolveViewCachePolicy,
  sameInstanceList,
} from './editor-cache';
import { parseInstanceId } from './editor-tabs';

export interface EditorSlotProps {
  activeViewId: string | null;
  activeView: ResolvedView | undefined;
  /**
   * All editor-area view declarations (§5.7): kept-alive hidden instances
   * resolve their own declaration here (the active one alone is not enough).
   */
  editorViews: readonly ResolvedView[];
  /** Serialized handles of migrated surfaces (§5.8); see `detachedSurfaceKey`. */
  detachedSurfaces?: ReadonlySet<string>;
  /** Open/focus an editor view instance (§5.7); backs a view's OpenViewRequest routing. */
  onOpenView?: (viewId: string, instanceKey?: string) => void;
  onFocusDetached?: (viewId: string, instanceKey?: string) => void;
  onAttachView?: (viewId: string, instanceKey?: string) => void;
  onDetachView?: (
    viewId: string,
    moduleId: string,
    instanceKey?: string,
  ) => void;
  registry: UiRegistry;
  moduleService: IModuleService;
  iframeBridge?: IframeBridge;
  externalBridge?: ExternalBridge;
  detachBridge?: DetachBridge;
  /** Subscribe to contribution-point changes (见 17); backs `ViewProps.contributions`. */
  subscribeContributionsChanged?: (listener: () => void) => () => void;
}

/**
 * Editor area slot (架构方案 §5.7): the active-view host with an MRU keep-alive
 * cache (§5.7 视图缓存). The active instance renders visibly; recently used
 * `keep-alive` instances stay mounted but hidden (`visibility: hidden` panes,
 * preserving React tree / iframe DOM and scroll state) so switching back is
 * instant. Eviction (LRU over capacity, view disappearance, detach, or a
 * contribution change) unmounts the pane, which runs the standard
 * `deactivateView`/`deactivateForView` teardown (§5.4) — so lifecycle semantics
 * shift from "deactivate on switch-away" to "deactivate on eviction/close" for
 * cached views only; `cache: 'none'` views keep the historical unmount-on-switch.
 * A detached active view shows a placeholder with Focus/Attach instead of
 * inline content.
 *
 * This whole slot lives on the editor area's structural anchor, which is a peer
 * of the native document tabs (见 20 二期). That anchor therefore renders with
 * `renderer: 'always'` (见 `area-specs.ts`) — under dockview's default the panes
 * cached here would be pulled out of the document whenever a document tab is
 * active, which is exactly the state this cache exists to preserve.
 */
export function EditorSlot({
  activeViewId,
  activeView,
  editorViews,
  detachedSurfaces,
  onOpenView,
  onFocusDetached,
  onAttachView,
  onDetachView,
  registry,
  moduleService,
  iframeBridge,
  externalBridge,
  detachBridge,
  subscribeContributionsChanged,
}: EditorSlotProps): ReactNode {
  const { t, title } = useTranslator();

  const activeInstanceId = activeViewId ?? activeView?.id ?? null;
  const activeInstanceKey = activeInstanceId
    ? parseInstanceId(activeInstanceId).instanceKey
    : undefined;
  const activeDetached =
    activeView !== undefined &&
    isDetachedInstance(detachedSurfaces, activeView.id, activeInstanceKey);

  const findView = (instanceId: string): ResolvedView | undefined =>
    editorViews.find((view) => view.id === parseInstanceId(instanceId).viewId);

  // Whether an instance may stay mounted while hidden (§5.7 视图缓存): its
  // declaration must still exist, it must not be detached (its content lives in
  // another window), and its effective policy must be keep-alive. Detachment is
  // checked per instance — one popped-out browser tab must not evict its
  // siblings' kept-alive panes (§5.8).
  const isCacheable = (instanceId: string): boolean => {
    const view = findView(instanceId);
    if (
      !view ||
      isDetachedInstance(
        detachedSurfaces,
        view.id,
        parseInstanceId(instanceId).instanceKey,
      )
    ) {
      return false;
    }
    return resolveViewCachePolicy(view) === 'keep-alive';
  };

  // MRU-ordered mounted instances (head = active). Derived during render (the
  // React "adjust state while rendering" pattern) so a switch mounts the new
  // active and keeps the previous one hidden within the same commit — no
  // transient frame where the old instance unmounts and remounts.
  const [mounted, setMounted] = useState<readonly string[]>([]);
  const plannedMounted = planMountedInstances(
    mounted,
    // A detached active view renders in its own window: its inline instance
    // must not mount here, so plan with no active (its cache entry is also
    // dropped via `isCacheable`).
    activeDetached ? null : activeInstanceId,
    isCacheable,
  );
  if (!sameInstanceList(plannedMounted, mounted)) {
    setMounted(plannedMounted);
  }

  // Conservative event-driven eviction (§5.7 视图缓存): on any contribution
  // change (module install/uninstall/update, 见 12/17) clear every hidden
  // instance — a dynamically updated module may keep its view id while its UI
  // bundle changed, and a stale kept-alive tree must not survive that. The
  // active instance is kept; the workbench already reconciles it against the
  // live view set.
  const activeRef = useRef(activeInstanceId);
  activeRef.current = activeInstanceId;
  useEffect(() => {
    if (!subscribeContributionsChanged) {
      return;
    }
    return subscribeContributionsChanged(() => {
      setMounted((prev) =>
        removeInstances(prev, (id) => id !== activeRef.current),
      );
    });
  }, [subscribeContributionsChanged]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-transparent">
      <div className="relative min-h-0 flex-1">
        {/* Stacked panes: every mounted instance keeps its own scroll container;
            hidden ones use `visibility: hidden` (not `display: none`) so layout,
            scroll positions and iframe rendering state are preserved. Rendered
            in `renderOrder` (stable, not MRU) — a keyed reorder would make
            React move the pane's DOM node, and a moved <iframe> reloads.

            The *shown* pane leaves both properties unset rather than asserting
            `visible`/`auto`. They inherit, so an explicit value here would
            override an ancestor that hid us — and this whole slot sits inside a
            dockview panel that hides exactly that way (`visibility: hidden` +
            `pointer-events: none` on the overlay container) whenever a sibling
            document tab is active. Asserting the values made the active pane
            paint straight through the document on top of it. */}
        {renderOrder(plannedMounted).map((instanceId) => {
          const view = findView(instanceId);
          if (!view) {
            return null;
          }
          const hidden = activeDetached || instanceId !== activeInstanceId;
          return (
            <div
              key={instanceId}
              className="absolute inset-0 flex min-h-0 flex-col overflow-auto"
              style={{
                visibility: hidden ? 'hidden' : undefined,
                pointerEvents: hidden ? 'none' : undefined,
                zIndex: hidden ? undefined : 1,
              }}
            >
              <SurfaceView
                viewId={view.id}
                moduleId={view.moduleId}
                instanceKey={parseInstanceId(instanceId).instanceKey}
                hidden={hidden}
                registry={registry}
                moduleService={moduleService}
                onOpenView={onOpenView}
                detachable={view.detachable}
                onDetach={onDetachView}
                kind={view.kind}
                url={view.url}
                autoLaunch={view.autoLaunch}
                externalEntry={view.externalEntry}
                iframeBridge={iframeBridge}
                externalBridge={externalBridge}
                detachBridge={detachBridge}
                subscribeContributionsChanged={subscribeContributionsChanged}
              />
            </div>
          );
        })}
        {activeView && activeDetached ? (
          <div className="absolute inset-0">
            <DetachedPlaceholder
              title={title(
                activeView.moduleId,
                activeView.title ?? activeView.id,
              )}
              onFocusDetached={
                onFocusDetached
                  ? () => onFocusDetached(activeView.id, activeInstanceKey)
                  : undefined
              }
              onAttachView={
                onAttachView
                  ? () => onAttachView(activeView.id, activeInstanceKey)
                  : undefined
              }
            />
          </div>
        ) : null}
        {!activeView ? (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-[13px] text-muted-foreground">
            {t('workbench.editor.openHint')}
          </div>
        ) : null}
      </div>
    </div>
  );
}
